import React, { ReactNode, useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";

// Mobile breakpoint hook
function useIsMobile(breakpoint: number = 768): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [breakpoint]);

  return isMobile;
}

interface SplitScreenLayoutProps {
  appearance?: "default" | "alpha";
  /** The content to display in the sidebar panel */
  sidebarContent: ReactNode;
  /** The main content to display */
  mainContent: ReactNode;
  /** Whether the sidebar is currently visible */
  isSidebarOpen: boolean;
  /** Width of the sidebar when open. Defaults to "33.333%". Used if left/right specific widths not provided */
  sidebarWidth?: string;
  /** Width of the left sidebar when open. Overrides sidebarWidth for left position */
  leftSidebarWidth?: string;
  /** Width of the right sidebar when open. Overrides sidebarWidth for right position */
  rightSidebarWidth?: string;
  /** Minimum width of the sidebar in pixels. Defaults to 350 */
  sidebarMinWidth?: number;
  /** Maximum width of the sidebar. Defaults to "50%" */
  sidebarMaxWidth?: string;
  /** Position of the sidebar. Defaults to "left" */
  sidebarPosition?: "left" | "right";
  /** Animation duration in milliseconds. Defaults to 300 */
  animationDuration?: number;
  /** Animation easing function. Defaults to "ease-out" */
  animationEasing?: string;
  /** Minimum width of the main content area in pixels. Defaults to 400 */
  mainContentMinWidth?: number;
  /** Callback when overlay backdrop is clicked on mobile */
  onCloseSidebar?: () => void;
}

const SplitScreenLayout: React.FC<SplitScreenLayoutProps> = ({
  appearance = "default",
  sidebarContent,
  mainContent,
  isSidebarOpen,
  sidebarWidth = "33.333%",
  leftSidebarWidth,
  rightSidebarWidth,
  sidebarMinWidth = 350,
  sidebarMaxWidth = "50%",
  sidebarPosition = "left",
  animationDuration = 300,
  animationEasing = "ease-out",
  mainContentMinWidth = 400,
  onCloseSidebar,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const isMobile = useIsMobile();


  // Resolve widths - specific overrides general
  const resolvedLeftWidth = leftSidebarWidth ?? sidebarWidth;
  const resolvedRightWidth = rightSidebarWidth ?? sidebarWidth;

  // Compute which sidebar should be open
  const isLeftOpen = isSidebarOpen && sidebarPosition === "left";
  const isRightOpen = isSidebarOpen && sidebarPosition === "right";

  // Build transition string
  const transition = `width ${animationDuration}ms ${animationEasing}, min-width ${animationDuration}ms ${animationEasing}`;

  // MOBILE: Full-screen overlay pattern
  if (isMobile) {
    const mobileTransition = `transform ${animationDuration}ms ${animationEasing}`;
    
    return (
      <div className={appearance === "alpha"? `h-full relative overflow-hidden bg-v7-paper` : `h-full relative overflow-hidden ${isDarkMode ?"bg-rp-black" : "bg-gray-50"}`}>
        {/* Main Content - always visible */}
        <div className="h-full w-full overflow-y-auto">
          {mainContent}
        </div>

        {/* Backdrop overlay */}
        {isSidebarOpen && (
          <div
            className={appearance === "alpha" ? "absolute inset-0 bg-v7-ink/60 z-40" : "absolute inset-0 bg-black/50 z-40"}
            onClick={onCloseSidebar}
            style={{
              transition: `opacity ${animationDuration}ms ${animationEasing}`,
            }}
          />
        )}

        {/* Sidebar as slide-over panel */}
        <div
          inert={!isSidebarOpen}
          className={appearance === "alpha" ? `absolute top-0 ${sidebarPosition === "left" ? "left-0" : "right-0"} h-full w-full max-w-sm z-50 bg-v7-white border-v7-ink/15 ${sidebarPosition ==="left" ? "border-r" : "border-l"}` : `absolute top-0 ${sidebarPosition === "left" ? "left-0" : "right-0"} h-full w-full max-w-sm z-50 ${
            isDarkMode ? "bg-rp-black border-rp-separator" : "bg-white border-gray-200"
          } ${sidebarPosition === "left" ? "border-r" : "border-l"}`}
          style={{
            transform: isSidebarOpen
              ? "translateX(0)"
              : sidebarPosition === "left"
              ? "translateX(-100%)"
              : "translateX(100%)",
            transition: mobileTransition,
            pointerEvents: isSidebarOpen ? "auto" : "none",
          }}
          aria-hidden={!isSidebarOpen}
        >
          <div className="h-full overflow-y-auto">
            {sidebarContent}
          </div>
        </div>
      </div>
    );
  }

  // DESKTOP: Original side-by-side layout
  return (
    <div className={appearance === "alpha"? `h-full flex overflow-hidden bg-v7-paper` : `h-full flex overflow-hidden ${isDarkMode ?"bg-rp-black" : "bg-gray-50"}`}>
      {/* Left Sidebar - ALWAYS in DOM for animation */}
      <div
        inert={!isLeftOpen}
        style={{
          width: isLeftOpen ? resolvedLeftWidth : 0,
          minWidth: isLeftOpen ? sidebarMinWidth : 0,
          maxWidth: sidebarMaxWidth,
          transition,
          pointerEvents: isLeftOpen ? "auto" : "none",
        }}
        className={appearance === "alpha"? `h-full overflow-hidden ${isLeftOpen ? `border-r border-v7-ink/15` :""}` : `h-full overflow-hidden ${
          isLeftOpen ? `border-r ${isDarkMode ? "border-rp-separator" : "border-gray-200"}` : ""
        }`}
        aria-hidden={!isLeftOpen}
      >
        <div className="h-full overflow-y-auto" style={{ minWidth: sidebarMinWidth }}>
          {sidebarPosition === "left" && sidebarContent}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 h-full overflow-hidden" style={{ minWidth: mainContentMinWidth }}>
        <div className="h-full w-full overflow-y-auto">{mainContent}</div>
      </div>

      {/* Right Sidebar - ALWAYS in DOM for animation */}
      <div
        inert={!isRightOpen}
        style={{
          width: isRightOpen ? resolvedRightWidth : 0,
          minWidth: isRightOpen ? sidebarMinWidth : 0,
          maxWidth: sidebarMaxWidth,
          transition,
          pointerEvents: isRightOpen ? "auto" : "none",
        }}
        className={appearance === "alpha"? `h-full overflow-hidden ${isRightOpen ? `border-l border-v7-ink/15` :""}` : `h-full overflow-hidden ${
          isRightOpen ? `border-l ${isDarkMode ? "border-rp-separator" : "border-gray-200"}` : ""
        }`}
        aria-hidden={!isRightOpen}
      >
        <div className="h-full overflow-y-auto" style={{ minWidth: sidebarMinWidth }}>
          {sidebarPosition === "right" && sidebarContent}
        </div>
      </div>
    </div>
  );
};

export default SplitScreenLayout;
